/**
 * Lightweight Prebid.js auction client.
 * Loads Prebid only when auction plan is enabled + bidders present.
 * Never invents fill — empty auction → no ads.
 */

var prebidLoadPromise = null;

function loadPrebidScript(url) {
  if (typeof window === "undefined") {
    return Promise.resolve(null);
  }
  if (window.pbjs) {
    return Promise.resolve(window.pbjs);
  }
  if (prebidLoadPromise) return prebidLoadPromise;
  prebidLoadPromise = new Promise(function (resolve) {
    var script = document.createElement("script");
    script.async = true;
    script.src = url || "https://cdn.jsdelivr.net/npm/prebid.js@8.52.0/dist/prebid.js";
    script.onload = function () {
      resolve(window.pbjs || null);
    };
    script.onerror = function () {
      prebidLoadPromise = null;
      resolve(null);
    };
    document.head.appendChild(script);
  });
  return prebidLoadPromise;
}

/**
 * @param {object} plan from ads/prebid
 * @param {object} [options]
 * @returns {Promise<{ filled: boolean, bids: array, reason?: string }>}
 */
export function runPrebidAuction(plan, options) {
  options = options || {};
  plan = plan || {};
  if (!plan.enabled) {
    return Promise.resolve({ filled: false, bids: [], reason: "prebid_disabled" });
  }
  if (!plan.adUnits || !plan.adUnits.length) {
    return Promise.resolve({
      filled: false,
      bids: [],
      reason: plan.reason || "prebid_no_units",
    });
  }

  return loadPrebidScript(options.scriptUrl).then(function (pbjs) {
    if (!pbjs) {
      return { filled: false, bids: [], reason: "prebid_sdk_unavailable" };
    }
    return new Promise(function (resolve) {
      pbjs.que = pbjs.que || [];
      pbjs.que.push(function () {
        try {
          pbjs.addAdUnits(plan.adUnits);
          pbjs.requestBids({
            timeout: plan.timeout_ms || 1200,
            adUnitCodes: plan.adUnits.map(function (u) {
              return u.code;
            }),
            bidsBackHandler: function (bids) {
              var list = [];
              Object.keys(bids || {}).forEach(function (code) {
                (bids[code] || []).forEach(function (b) {
                  list.push(b);
                });
              });
              resolve({
                filled: list.length > 0,
                bids: list,
                reason: list.length ? null : "prebid_no_bids",
              });
            },
          });
        } catch (e) {
          resolve({
            filled: false,
            bids: [],
            reason: "prebid_error",
          });
        }
      });
    });
  });
}

export default { runPrebidAuction: runPrebidAuction };
